import {
  CLOSURE_FACTOR, DEMAND_SHIFT_CHANCE, DEMAND_SHIFT_FIRST_DAY, DEMAND_STREAM,
  RETIRE_CHANCE, RETIRE_FIRST_DAY, WORLD_STREAM,
  DISRUPTION_CHANCE, DISRUPTION_DAYS_MAX, DISRUPTION_DAYS_MIN,
  DISRUPTION_FACTOR_SPREAD, DISRUPTION_FIRST_DAY, DISRUPTION_STREAM, FUEL_FACTOR,
  OUTAGE_FACTOR, SLUMP_FACTOR, SUBSIDY_FACTOR, THROUGHPUT_CHANCE,
  THROUGHPUT_DAYS_MAX, THROUGHPUT_DAYS_MIN, THROUGHPUT_FIRST_DAY,
  THROUGHPUT_STREAM,
} from "./constants";
import { rng } from "./rng";
import type {
  Disruption, DisruptionKind, ThroughputDisruption, ThroughputKind,
} from "./types";

/**
 * Weather, derived rather than stored (RFC-0011).
 *
 * A schedule in the save would need a migration, validation, bytes in every
 * autosave and a horizon that runs out — all to hold something `seed` and `day`
 * already determine. So this module is a pure function of those two numbers:
 * step() rolls dice without owning a die, a save loaded mid-disruption re-derives
 * the same one, and the balancing guard stays deterministic.
 *
 * No GameState in here on purpose. sim.ts passes the two numbers, and ui/ may
 * call this directly — a pure core function is not the upward call rule 1
 * forbids.
 */

/**
 * Every disruption covering `day`, oldest first.
 *
 * Only the last DISRUPTION_DAYS_MAX days are rolled, because nothing lasts
 * longer than that: the cost of a call is the same on day 10 and on day 10 000.
 */
export function disruptionsOn(seed: number, day: number): Disruption[] {
  const out: Disruption[] = [];
  const first = Math.max(DISRUPTION_FIRST_DAY, day - DISRUPTION_DAYS_MAX + 1);

  for (let start = first; start <= day; start++) {
    // Multiplied, not added. Adding both inputs into one number makes
    // (seed + 1, day) and (seed, day + 1) the same stream — see the warning on
    // DISRUPTION_STREAM. This is the one line here that must not be "simplified".
    const r = rng((seed ^ Math.imul(start, DISRUPTION_STREAM)) >>> 0);

    // Draw order is load-bearing: trigger, kind, duration, factor. Changing it
    // rewrites the history of every world that ever existed, silently. New
    // parameters are appended, never inserted.
    if (r() >= DISRUPTION_CHANCE) continue;
    const kind: DisruptionKind = r() < 0.5 ? "fuel" : "subsidy";
    const span = DISRUPTION_DAYS_MAX - DISRUPTION_DAYS_MIN + 1;
    const days = DISRUPTION_DAYS_MIN + Math.floor(r() * span);
    if (start + days <= day) continue; // already over

    const base = kind === "fuel" ? FUEL_FACTOR : SUBSIDY_FACTOR;
    // The jitter varies the distance from 1, never the factor itself: scaling a
    // 1.5 factor by 0.85 is fine, but scaling a 0.8 one by 1.15 crosses over and
    // turns a rebate into a surcharge. This way each kind stays on its own side
    // of 1 whatever the constants become, and the mean is exactly `base`.
    const jitter = 1 - DISRUPTION_FACTOR_SPREAD + 2 * DISRUPTION_FACTOR_SPREAD * r();
    out.push({ kind, startDay: start, days, factor: 1 + (base - 1) * jitter });
  }

  return out;
}

/**
 * What today does to daily upkeep. Both kinds land here — fuel above 1, the
 * rebate below — because both are the same axis pulled in opposite directions,
 * which is what keeps a disruption out of the delivery price and therefore out of
 * RFC-0008's arithmetic.
 *
 * Overlapping disruptions multiply: nothing here can make the map unsolvable, so
 * a spike during a rebate is a story rather than a bug. With none active this
 * returns exactly 1, which is why a world before day 10 behaves bit for bit as it
 * did before this RFC.
 */
export const upkeepFactor = (active: readonly Disruption[]): number =>
  active.reduce((f, d) => f * d.factor, 1);

/** True while this kind is one of the active ones. For the HUD chip. */
export const hasKind = (active: readonly Disruption[], kind: DisruptionKind): boolean =>
  active.some((d) => d.kind === kind);

/** Days left including today. Player-facing arithmetic, hence not in the UI. */
export const daysLeft = (d: Disruption, day: number): number =>
  d.startDay + d.days - day;

// ─── Throughput (RFC-0012) ──────────────────────────────────────────────────

/**
 * Draw order, and therefore load-bearing in the same way as above: appended to,
 * never reordered.
 */
const KINDS: readonly ThroughputKind[] = ["outage", "slump", "closure"];

const FACTOR: Record<ThroughputKind, number> = {
  outage: OUTAGE_FACTOR,
  slump: SLUMP_FACTOR,
  closure: CLOSURE_FACTOR,
};

/**
 * The one throughput disruption covering `day`, or null.
 *
 * Deliberately not a list. Where fuel and a rebate may overlap because they only
 * argue about the bill, two outages at once can take more production than
 * DEMAND_SUPPLY_RATIO leaves over and make the map unsolvable — the failure
 * test/balance.test.ts exists for. Rolling the oldest candidate first and
 * returning it means "at most one" is construction rather than a check that
 * something else has to run. RFC-0012 §3.1.
 *
 * Its own stream, and nothing above this line is touched: an extra draw inside
 * disruptionsOn() would rewrite the weather of every world that already exists.
 */
export function throughputOn(seed: number, day: number): ThroughputDisruption | null {
  const first = Math.max(THROUGHPUT_FIRST_DAY, day - THROUGHPUT_DAYS_MAX + 1);

  for (let start = first; start <= day; start++) {
    const r = rng((seed ^ Math.imul(start, THROUGHPUT_STREAM)) >>> 0);

    // Trigger, kind, duration, target. New parameters are appended, never
    // inserted — the same rule and the same reason as in disruptionsOn().
    if (r() >= THROUGHPUT_CHANCE) continue;
    const kind = KINDS[Math.floor(r() * KINDS.length)] ?? "outage";
    const span = THROUGHPUT_DAYS_MAX - THROUGHPUT_DAYS_MIN + 1;
    const days = THROUGHPUT_DAYS_MIN + Math.floor(r() * span);
    if (start + days <= day) continue; // already over

    // No jitter on this factor. On the upkeep axis a spread keeps the two kinds
    // apart; here it would move OUTAGE_FACTOR, and that constant is bounded by
    // the solvability arithmetic rather than chosen for feel.
    return { kind, startDay: start, days, factor: FACTOR[kind], target: r() };
  }

  return null;
}

/** Days left including today. Same shape as daysLeft() for the cost kinds. */
export const throughputDaysLeft = (d: ThroughputDisruption, day: number): number =>
  d.startDay + d.days - day;

// ─── Demand shift (RFC-0016) ────────────────────────────────────────────────

/**
 * The two cities that trade appetites on this day, or null.
 *
 * Two fractions in [0, 1), not node ids and not list indices — this module knows
 * no map, the same indirection `pickTarget()` uses and for the same reason.
 * sim.ts owns the city list and turns the pair into two *distinct* cities.
 *
 * No look-back loop, unlike the two above, and that is the real difference
 * between a shift and weather. Weather is re-derived every frame for as long as
 * it lasts, so it has to be findable from any day it covers. A swap is applied
 * once, at the day boundary, and then it *is* the world — asking again the next
 * day would apply it twice.
 *
 * Its own stream. See DEMAND_STREAM.
 */
export function demandSwapOn(seed: number, day: number): [number, number] | null {
  if (day < DEMAND_SHIFT_FIRST_DAY) return null;
  const r = rng((seed ^ Math.imul(day, DEMAND_STREAM)) >>> 0);
  // Trigger first, then the pair — appended to, never reordered, the same rule
  // as the two functions above.
  if (r() >= DEMAND_SHIFT_CHANCE) return null;
  return [r(), r()];
}

// ─── The world takes one back (RFC-0017 §3.5) ───────────────────────────────

/**
 * The producer that closes on this day, as a fraction in [0, 1), or null.
 *
 * A fraction rather than an id, for the same reason `pickTarget()` takes one:
 * this module knows no map. sim.ts turns it into a node and is also where the
 * bounds live — whether that particular works *may* go is a question about the
 * board, not about the dice.
 *
 * No look-back loop, like the demand swap and unlike the weather: a closure is
 * applied once and is then simply the world. Asking again tomorrow would close
 * a second works every day for the rest of the game.
 */
export function retirementOn(seed: number, day: number): number | null {
  if (day < RETIRE_FIRST_DAY) return null;
  const r = rng((seed ^ Math.imul(day, WORLD_STREAM)) >>> 0);
  // Trigger first, then the target — appended to, never reordered, the same
  // rule as every other stream in this file.
  if (r() >= RETIRE_CHANCE) return null;
  return r();
}
